import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { ekycCauHinhRepository } from '../../../lib/repositories/ekyc-cau-hinh.repository';
import type {
  ChinhSach,
  QuyTrinh,
  QuyTrinhSua,
  QuyTrinhTao,
  UngDung,
  UngDungCoBiMat,
  UngDungSua,
  UngDungTao,
} from '../types';

/** Khoá cache của phần cấu hình, nằm dưới `['ekyc']` như phần còn lại của module. */
export const KHOA_CAU_HINH = {
  danhMuc: ['ekyc', 'quy-trinh', 'danh-muc'] as const,
  quyTrinh: (ungDungId: string) => ['ekyc', 'quy-trinh', 'ds', ungDungId] as const,
  xemTruoc: (buoc: Record<string, boolean>, chinhSach: ChinhSach | null) =>
    ['ekyc', 'quy-trinh', 'xem-truoc', buoc, chinhSach] as const,
  ungDung: ['ekyc', 'ung-dung'] as const,
  suDung: (id: string) => ['ekyc', 'ung-dung', 'su-dung', id] as const,
  luotGiao: (id: string) => ['ekyc', 'ung-dung', 'gui-webhook', id] as const,
};

/** 4xx là câu trả lời của server: thử lại chỉ bắt người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Danh mục bước đổi theo bản module, không theo lượt bấm: giữ lâu. */
export function useDanhMucQuyTrinh() {
  return useQuery({
    queryKey: KHOA_CAU_HINH.danhMuc,
    queryFn: () => ekycCauHinhRepository.danhMuc(),
    staleTime: 600_000,
    retry: thuLai,
  });
}

export function useDsUngDung(bat = true) {
  return useQuery({
    queryKey: KHOA_CAU_HINH.ungDung,
    queryFn: () => ekycCauHinhRepository.dsUngDung(),
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

export function useDsQuyTrinh(ungDungId: string | null) {
  return useQuery({
    queryKey: KHOA_CAU_HINH.quyTrinh(ungDungId ?? ''),
    queryFn: () => ekycCauHinhRepository.dsQuyTrinh(ungDungId!),
    enabled: ungDungId !== null,
    staleTime: 15_000,
    retry: thuLai,
  });
}

/**
 * Xem trước phía server, khoá theo **chính thân yêu cầu**: cùng cấu hình thì cùng kết quả, đổi một công tắc là một
 * khoá mới. Phía gọi tự giãn nhịp (đợi người dùng ngừng gõ) trước khi đổi tham số.
 *
 * `keepPreviousData`: trong lúc chờ kết quả mới, màn vẫn vẽ bản cũ thay vì nháy về skeleton mỗi lần bấm.
 */
export function useXemTruocQuyTrinh(buoc: Record<string, boolean>, chinhSach: ChinhSach | null) {
  return useQuery({
    queryKey: KHOA_CAU_HINH.xemTruoc(buoc, chinhSach),
    queryFn: () => ekycCauHinhRepository.xemTruoc(buoc, chinhSach!),
    enabled: chinhSach !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    retry: thuLai,
  });
}

/** Mọi lượt ghi quy trình đổi cả danh sách quy trình lẫn con số "quy trình mặc định" ở màn ứng dụng. */
export function useGhiQuyTrinh(ungDungId: string | null) {
  const qc = useQueryClient();
  const lamMoi = () => {
    if (ungDungId) void qc.invalidateQueries({ queryKey: KHOA_CAU_HINH.quyTrinh(ungDungId) });
    void qc.invalidateQueries({ queryKey: KHOA_CAU_HINH.ungDung, exact: true });
  };
  return {
    tao: useMutation<QuyTrinh, ApiError, QuyTrinhTao>({
      mutationFn: (du) => ekycCauHinhRepository.taoQuyTrinh(du), onSuccess: lamMoi,
    }),
    sua: useMutation<QuyTrinh, ApiError, { id: string; du: QuyTrinhSua }>({
      mutationFn: ({ id, du }) => ekycCauHinhRepository.suaQuyTrinh(id, du), onSuccess: lamMoi,
    }),
    datMacDinh: useMutation<QuyTrinh, ApiError, string>({
      mutationFn: (id) => ekycCauHinhRepository.datMacDinh(id), onSuccess: lamMoi,
    }),
    xoa: useMutation<void, ApiError, string>({
      mutationFn: (id) => ekycCauHinhRepository.xoaQuyTrinh(id), onSuccess: lamMoi,
    }),
  };
}

export function useSuDungUngDung(id: string | null) {
  return useQuery({
    queryKey: KHOA_CAU_HINH.suDung(id ?? ''),
    queryFn: () => ekycCauHinhRepository.suDung(id!),
    enabled: id !== null,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Lượt giao còn đang chờ thì hỏi lại theo nhịp; hết chờ thì dừng. */
export function useLuotGiaoWebhook(id: string | null) {
  return useQuery({
    queryKey: KHOA_CAU_HINH.luotGiao(id ?? ''),
    queryFn: () => ekycCauHinhRepository.luotGiao(id!),
    enabled: id !== null,
    staleTime: 10_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data?.items.some((w) => w.trangThai === 'PENDING') ? 10_000 : false),
  });
}

/**
 * Lượt ghi ứng dụng. `tao` và `xoay` trả bí mật webhook: phía gọi giữ nó trong state của hộp hiện bí mật, **không**
 * ghi vào cache — `onSuccess` ở đây chỉ làm mới danh sách, không `setQueryData` phản hồi.
 */
export function useGhiUngDung() {
  const qc = useQueryClient();
  const lamMoi = () => void qc.invalidateQueries({ queryKey: KHOA_CAU_HINH.ungDung });
  return {
    tao: useMutation<UngDungCoBiMat, ApiError, UngDungTao>({
      mutationFn: (du) => ekycCauHinhRepository.taoUngDung(du), onSuccess: lamMoi,
    }),
    sua: useMutation<UngDung, ApiError, { id: string; du: UngDungSua }>({
      mutationFn: ({ id, du }) => ekycCauHinhRepository.suaUngDung(id, du), onSuccess: lamMoi,
    }),
    xoay: useMutation<UngDungCoBiMat, ApiError, string>({
      mutationFn: (id) => ekycCauHinhRepository.xoayBiMat(id), onSuccess: lamMoi,
    }),
    batTat: useMutation<UngDung, ApiError, { id: string; bat: boolean }>({
      mutationFn: ({ id, bat }) => ekycCauHinhRepository.batTat(id, bat), onSuccess: lamMoi,
    }),
    thuLai: useMutation<unknown, ApiError, string>({
      mutationFn: (id) => ekycCauHinhRepository.thuLaiWebhook(id), onSuccess: lamMoi,
    }),
  };
}

/** Giá trị đi theo `v` nhưng trễ `ms`: chỉ đổi khi người dùng ngừng gõ — để xem trước không hỏi máy chủ mỗi phím. */
export function useGianNhip<T>(v: T, ms: number): T {
  const [tre, setTre] = useState(v);
  useEffect(() => {
    const h = window.setTimeout(() => setTre(v), ms);
    return () => window.clearTimeout(h);
  }, [v, ms]);
  return tre;
}
